import { useState, useEffect, SetStateAction, useMemo } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { GlobalVariables } from 'Globals/GlobalVariables'

import Map from 'components/Map'
import { Filters } from 'components/filters'
import { RestosList } from 'components/restosList'
import { InfoLayer } from 'components/infoLayer'
import { device } from '../breakpoints'
import { MarkerData } from 'Types/markers'
import useFetch from 'react-fetch-hook'
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from '@mui/material/styles'

import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/button.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/form.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/modal.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/popup.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/checkbox.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/divider.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/card.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/transition.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/search.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/placeholder.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import './styles.scss'

import { Button, TextField, CircularProgress } from '@mui/material'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import CloseIcon from '@mui/icons-material/Close'
import NearMeIcon from '@mui/icons-material/NearMe'
import MapIcon from '@mui/icons-material/Map'
import ListIcon from '@mui/icons-material/List'
import locales from 'locales/locales'

const MainComponent = ({
  tenant,
  colorMode,
  properties,
}: MicroFrontendProps) => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null)
  const [mainContainer, setMainContainer] = useState<HTMLDivElement | null>(
    null
  )
  const targetDomain = properties?.publisherDomain
    ? properties?.publisherDomain
    : tenant?.key
  const publisherDomain = targetDomain ? targetDomain : '24heures' // '24heures' //
  const lang = tenant?.lang || 'fr'
  // const publisherDomain = '24heures'

  // Original markers list
  const [markers, setMarkers] = useState<MarkerData[]>([])
  const [metadata, setMetadata] = useState({})

  // Filtered makers list
  const [filteredMarkers, setFilteredMarkers] = useState<MarkerData[]>([])

  // Active marker, displayed in the info window and on the map
  const [activeMarker, setActiveMarker] = useState<MarkerData | null>(null)

  // Map navigation switch
  const [mapIsLocked, setMapIsLocked] = useState(true)

  // Map / list switch
  const [showMap, setShowMap] = useState(true)

  // Search box
  const [currentSearch, setCurrentSearch] = useState('')

  // User location
  const [userLocation, setUserLocation] = useState<{
    lat: number
    lng: number
  } | null>()

  // Mobile or not
  const isMobile = window.innerWidth < 768

  // Data fetching
  const time = useMemo(() => new Date().getTime(), [])
  const { isLoading, data, error } = useFetch(
    `https://wch-interactive.storage.googleapis.com/ouest-lausannois/markers.json?time=${time}`,
    {
      formatter: (response: Response) => response.json(),
    }
  )

  const handleSearchInput = (e: {
    target: { value: SetStateAction<string> }
  }) => {
    setCurrentSearch(e.target.value)
  }

  const [locateMe, setLocateMe] = useState(false)
  const [locateFailed, setLocateFailed] = useState(false)
  const handleUserLocationFound = (lat: number, lng: number) => {
    setUserLocation({ lat, lng })
    setLocateMe(false)
  }
  const handleUserLocationError = (e: GeolocationPositionError) => {
    alert(locales.unableToLoc[lang])
    console.warn(e)
    // We show button again but we disable it
    setLocateMe(false)
    setLocateFailed(true)
  }

  // Themes
  const { palette } = createTheme()
  const { augmentColor } = palette
  const createColor = (mainColor: any) =>
    augmentColor({ color: { main: mainColor } })
  const muiTheme = createTheme({
    typography: {
      fontFamily: 'var(--font-plex)',
      htmlFontSize: 9,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: '1.5rem',
            textTransform: 'none',
            margin: '0 6px',
            whiteSpace: 'nowrap',
            borderRadius: '0',
            padding: '1rem 1rem',
          },
        },
      },
      // Custom properties for light/dark <TextField />
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: '#334455',
          },
          input: {
            color: colorMode === 'dark' ? '#E9E9E9' : '#334455',
            background: colorMode === 'dark' ? '#222222' : '#f2f3f4',
            border: 0,
            '&::before': {
              borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
            },
          },
          notchedOutline: {
            border: 0,
            borderColor:
              colorMode === 'dark'
                ? 'rgba(200, 200, 200, 0.23)'
                : 'rgba(0, 0, 0, 0.23)',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: colorMode === 'dark' ? '#555555' : 'rgba(0, 0, 0, 0.6)',
          },
        },
      },
      MuiModal: {
        defaultProps: {
          container: ref,
        },
      },
    },
    palette: {
      primary: {
        main:
          publisherDomain === 'tdg'
            ? colorMode === 'dark'
              ? '#5A64A8'
              : '#2c3890'
            : colorMode === 'dark'
            ? '#A34746'
            : '#d9010e',
      },
      secondary: { main: colorMode === 'dark' ? '#E9E9E9' : '#334455' },
      buttonDark:
        colorMode === 'light' ? createColor('#202346') : createColor('#6f7187'),
      buttonLight: createColor('#efece0'),
      yellow: createColor('#ffe462'),
    },
  })

  const theme = {
    lang: lang,
    colorMode: colorMode,
    mainColor:
      publisherDomain === 'tdg'
        ? colorMode === 'dark'
          ? '#5A64A8'
          : '#2c3890'
        : colorMode === 'dark'
        ? '#A34746'
        : '#d9010e',
    textColor: colorMode === 'dark' ? '#E9E9E9' : '#202346',
    plainColor: colorMode === 'dark' ? '#121212' : '#ffffff',
    borderColor: colorMode === 'dark' ? '#555555' : '#f1f1f1',
    greyColor: colorMode === 'dark' ? '#222222' : '#f2f3f4',
    itemBorderColor: colorMode === 'dark' ? '#555555' : '#f6f7f8',
  }

  useEffect(() => {
    // Fill the filtered markers list
    setFilteredMarkers(markers)
  }, [markers])

  useEffect(() => {
    // Fill the markers list once we retrieve the markers data
    if (data) {
      const filteredData = data.markers.filter(
        (marker: MarkerData) =>
          !Number.isNaN(Number(marker.latitude)) &&
          !Number.isNaN(Number(marker.longitude))
      )
      if (filteredData.length < data.length) {
        console.warn('NaN geolocation were removed.')
      }
      setMarkers(filteredData)
      setMetadata(data.metadata)
    }
  }, [data])

  useEffect(() => {
    console.log(error)
  }, [error])

  return (
    <AppContainer id='mf-app-ouest-lausannois' ref={setRef}>
      <GlobalVariables colorMode={colorMode}>
        <ThemeProvider theme={theme}>
          <MuiThemeProvider theme={muiTheme}>
            <Container ref={setMainContainer}>
              <Toolbar
                showMap={showMap}
                style={{
                  transition: 'transform 0.3s ease-in-out',
                  transform: activeMarker
                    ? 'translateY(-300px)'
                    : 'translateY(0)',
                }}
              >
                <SearchField>
                  <TextField
                    fullWidth
                    value={currentSearch}
                    onChange={handleSearchInput}
                    id='outlined-basic'
                    label={locales.searchPlaceholder[lang]}
                    variant='outlined'
                    onKeyDown={(e: any) => {
                      e.key === 'Enter' && e.target.blur()
                    }}
                  />
                  <MenuBlock>
                    <Filters
                      markers={markers}
                      metadata={metadata}
                      handleFilter={(filteredMarkers: MarkerData[]) => {
                        setFilteredMarkers(filteredMarkers)
                      }}
                      currentSearch={currentSearch}
                    />

                    {isMobile && (
                      <>
                        <Button
                          color='buttonDark'
                          disableElevation
                          startIcon={showMap ? <ListIcon /> : <MapIcon />}
                          variant='contained'
                          onClick={() => {
                            setShowMap(!showMap)
                            // console.log(mainContainer?.getBoundingClientRect())
                            mainContainer?.scrollIntoView()
                            // window.scrollTo(0, 0)
                          }}
                        >
                          {showMap
                            ? locales.mapButtonList[theme.lang]
                            : locales.mapButtonMap[theme.lang]}
                        </Button>
                        {/* <Button
                          color='buttonDark'
                          disableElevation
                          startIcon={
                            !locateMe ? (
                              <NearMeIcon />
                            ) : (
                              <ProgressIcon>
                                <CircularProgress
                                  color='secondary'
                                  size={'.65em'}
                                />
                              </ProgressIcon>
                            )
                          }
                          variant='contained'
                          onClick={() => {
                            setLocateMe(true)
                            showMap
                              ? setLocateMe(true)
                              : navigator.geolocation.getCurrentPosition(
                                  (position) => {
                                    handleUserLocationFound(
                                      position.coords.latitude,
                                      position.coords.longitude
                                    )
                                  },
                                  (error) => {
                                    handleUserLocationError(error)
                                  }
                                )

                            window.dataLayer = window.dataLayer || []
                            window.dataLayer.push({
                              event: 'Interactions',
                              event_action: 'click',
                              event_label: `mylocationbutton`,
                            })
                          }}
                          disabled={locateFailed}
                        >
                          {!showMap
                            ? locales.mapButtonCloseby[theme.lang]
                            : locales.mapButtonLocateMe[theme.lang]}
                        </Button> */}
                      </>
                    )}
                  </MenuBlock>
                </SearchField>
                <div>
                  <ListResultCount>
                    {locales.searchResultCount[lang](filteredMarkers.length)}
                  </ListResultCount>
                </div>
              </Toolbar>

              {/* The map container with the map activation button and locate-me button */}
              {(showMap || !isMobile) && (
                <MapContainer>
                  <MapContent>
                    {/* The locate-me button */}
                    {/* {!isMobile && (
                      <MapGeolocateButtonContainer>
                        <Button
                          color='buttonDark'
                          disableElevation
                          variant='contained'
                          startIcon={
                            !locateMe ? (
                              <NearMeIcon />
                            ) : (
                              <ProgressIcon>
                                <CircularProgress
                                  color='secondary'
                                  size={'.65em'}
                                />
                              </ProgressIcon>
                            )
                          }
                          onClick={() => {
                            setLocateMe(true)

                            window.dataLayer = window.dataLayer || []
                            window.dataLayer.push({
                              event: 'Interactions',
                              event_action: 'click',
                              event_label: `mylocationbutton`,
                            })
                          }}
                          disabled={locateFailed}
                        >
                          {locales.mapButtonLocateMe[lang]}
                        </Button>
                      </MapGeolocateButtonContainer>
                    )} */}

                    {/* The map activation button */}
                    <MapActivationButtonContainer
                      onClick={() => {
                        setMapIsLocked(!mapIsLocked)

                        window.dataLayer = window.dataLayer || []
                        window.dataLayer.push({
                          event: 'Interactions',
                          event_action: 'click',
                          event_label: `mapactivationbutton`,
                        })
                      }}
                    >
                      <Button
                        color='yellow'
                        disableElevation
                        variant='contained'
                        startIcon={
                          mapIsLocked ? <ZoomOutMapIcon /> : <CloseIcon />
                        }
                      >
                        {mapIsLocked
                          ? isMobile
                            ? locales.mapButtonEnterNavigationModeShort[lang]
                            : locales.mapButtonEnterNavigationMode[lang]
                          : isMobile
                          ? locales.mapButtonExitNavigationModeShort[lang]
                          : locales.mapButtonExitNavigationMode[lang]}
                      </Button>
                    </MapActivationButtonContainer>

                    {/* The map */}
                    <Map
                      handleMapLoaded={() => {
                        console.log('Map loaded!')
                      }}
                      handleMarkerClick={(marker: any) => {
                        setActiveMarker(marker)
                      }}
                      handleUserLocationFound={handleUserLocationFound}
                      handleUserLocationError={handleUserLocationError}
                      markers={filteredMarkers}
                      activeMarker={activeMarker}
                      locked={mapIsLocked}
                      publisherDomain={publisherDomain}
                      locateMe={locateMe}
                    />
                  </MapContent>
                </MapContainer>
              )}

              {/* The list of restaurants */}
              <ListContainer>
                {isLoading ? (
                  <PlaceholderContainer>
                    <div className='ui two column stackable grid'>
                      {new Array(6).fill(0).map((_, i) => (
                        <div key={`placeholder-column-${i}`} className='column'>
                          <div className='ui raised segment'>
                            <div className='ui placeholder'>
                              <div className='image header'>
                                <div className='line'></div>
                                <div className='line'></div>
                              </div>
                              <div className='paragraph'>
                                <div className='medium line'></div>
                                <div className='short line'></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </PlaceholderContainer>
                ) : (
                  <div>
                    {(!showMap || !isMobile) && (
                      <RestosList
                        userLocation={userLocation}
                        items={filteredMarkers}
                        publisher={publisherDomain}
                        handleClick={(marker: MarkerData) => {
                          setActiveMarker(marker)
                          if (mainContainer) {
                            const b = mainContainer?.getBoundingClientRect()
                            if (b.top > 0) {
                              mainContainer.scrollIntoView()
                            }

                            if (b.top + b.height < window.innerHeight) {
                              window.scrollTo(
                                0,
                                b.top +
                                  window.scrollY +
                                  b.height -
                                  window.innerHeight
                              )
                            }
                          }
                        }}
                      />
                    )}
                  </div>
                )}
                {error && <div>{locales.loadingError[lang]}</div>}
              </ListContainer>

              <InfoLayer
                activeMarker={activeMarker}
                handleClose={() => {
                  setActiveMarker(null)
                }}
                publisher={publisherDomain}
                colorMode={colorMode}
              />
            </Container>
          </MuiThemeProvider>
        </ThemeProvider>
      </GlobalVariables>
    </AppContainer>
  )
}

const Container = styled.div`
  display: block;
  position: relative;
  margin-bottom: 60px;
`

const MapContainer = styled.div`
  position: relative;
  top: 0;
  z-index: 2;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    height: 100vh;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    height: 100%;
    width: 50%;
    position: absolute;
    top: 0;
    right: 0;
  }
`

const MapContent = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
`

const ListContainer = styled.div`
  top: 0;
  z-index: 99;
  background-color: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  line-height: 1.4em;
  position: relative;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 100%;
    margin-top: 0;
    min-height: auto;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 50%;
    margin-top: 0;
    min-height: auto;
  }
`

const PlaceholderContainer = styled.div`
  padding: 1em;
`

const AppContainer = styled.div`
  font-family: var(--font-plex);
  font-size: 1.8rem;
  line-height: 3rem;
  min-height: 100vh;
`

const Toolbar = styled.div<{ showMap: boolean }>`
  // border-bottom: 1px solid
  //   ${(props) =>
    props.theme.colorMode === 'light' ? '#d4d4d4' : '#ffffff33'};
  // border-top: 1px solid
  //   ${(props) =>
    props.theme.colorMode === 'light' ? '#d4d4d4' : '#ffffff33'};
  background-color: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  width: 100%;
  top: 0;
  z-index: 999;
  padding: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    position: ${(props) => (props.showMap ? 'absolute' : 'sticky')};
    top: 0;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    position: sticky;
    top: 0;
  }

  input {
    width: 100%;
  }
`

const MenuBlock = styled.div`
  align-items: center;
  justify-content: center;
  flex-direction: row-reverse;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    display: flex;
    margin-bottom: 12px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    display: block;
    margin-bottom: 0;
  }
`

const SearchField = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 700px;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    flex-direction: column-reverse;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    flex-direction: row;
  }
`

const MapGeolocateButtonContainer = styled.div`
  position: absolute;
  bottom: 80px;
  right: 10px;
  display: block;
  z-index: 10;
  cursor: pointer;
`

const ProgressContainer = styled.div`
  position: absolute;
  top: 0;
  right: 4rem;
  transition: all 1s';
`

const ProgressIcon = styled.div`
  line-height: 0;

  svg {
    circle {
      stroke: white !important;
    }
  }
`

const MapActivationButtonContainer = styled.div`
  position: absolute;
  z-index: 10;
  cursor: pointer;
  left: 50%;
  transform: translateX(-50%);

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    bottom: 50px;
    // display: none;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    bottom: 25px;
    // display: block;
  }
`

const ListResultCount = styled.div`
  text-align: left;
  font-size: 0.8em;
  opacity: 0.5;
  width: 100%;
  margin-top: 1em;
`

export default MainComponent
